import React, { Component } from 'react'
import { css, StyleSheet } from 'aphrodite'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tooltip from '@material-ui/core/Tooltip'

import { PageDialog } from '../../../common/components/PageDialog'
import TableCard from '../../../common/components/tables/TableCard'
import { AppButton } from '../../../common/components/AppButton'
import { ApiHelper } from '../../../common/helpers/ApiHelper'
import { Helper } from '../../../common/helpers/Helper'
import { CRMHelper } from '../../../helpers/CRMHelper'
import { CommonTheme } from '../../../styles/Styles'
import { AppTheme } from '../../../styles/AppTheme'

function HealthStatusCell({ status }) {
  const style = CRMHelper.getHealthStatusStyle(status)
  return (
    <div className={css(Styles.statusCell)}>
      <Tooltip title={style.tooltip} placement='top'>
        <span
          className={css(Styles.healthDot)}
          style={{ backgroundColor: style.color }}
        />
      </Tooltip>
      <span>{style.label}</span>
    </div>
  )
}

function InboxHealthHistoryBody(props) {
  return <InboxHealthHistoryContent {...props} />
}

class InboxHealthHistoryContent extends Component {
  constructor(props) {
    super(props)
    this.state = {
      fetchState: ApiHelper.State.LOADING,
      errMsg: '',
      items: [],
      nextPage: null,
      nextPageState: ApiHelper.State.READY,
    }
    this.limit = 50
  }

  componentDidMount() {
    this.setSubtitle()
    this.fetchHistory()
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.inboxId !== this.props.inboxId
      || prevProps.orgId !== this.props.orgId
    ) {
      this.setSubtitle()
      this.setState({
        fetchState: ApiHelper.State.LOADING,
        errMsg: '',
        items: [],
        nextPage: null,
        nextPageState: ApiHelper.State.READY,
      }, () => this.fetchHistory())
    } else if (prevProps.inboxEmail !== this.props.inboxEmail) {
      this.setSubtitle()
    }
  }

  setSubtitle() {
    if (!this.props.setDialogHeader) return
    const email = this.props.inboxEmail || this.props.inboxId || ''
    this.props.setDialogHeader(
      email
        ? <div className={css(Styles.subtitle)}>{email}</div>
        : null
    )
  }

  async fetchHistory({ append = false } = {}) {
    const { orgId, inboxId } = this.props
    if (!orgId || !inboxId) {
      this.setState({
        fetchState: ApiHelper.State.ERROR,
        errMsg: 'Missing inbox',
      })
      return
    }

    if (append) {
      if (!this.state.nextPage || this.state.nextPageState === ApiHelper.State.LOADING) return
      this.setState({ nextPageState: ApiHelper.State.LOADING })
    } else {
      this.setState({ fetchState: ApiHelper.State.LOADING, errMsg: '' })
    }

    try {
      const result = await CRMHelper.fetchInboxHealthHistory({
        orgId,
        inboxId,
        nextPage: append ? this.state.nextPage : null,
        limit: this.limit,
      })
      const nextItems = Array.isArray(result.items) ? result.items : []
      this.setState(prev => ({
        fetchState: ApiHelper.State.READY,
        nextPageState: ApiHelper.State.READY,
        items: append ? (prev.items || []).concat(nextItems) : nextItems,
        nextPage: result.nextPage || null,
        errMsg: '',
      }))
    } catch (e) {
      this.setState({
        fetchState: append ? ApiHelper.State.READY : ApiHelper.State.ERROR,
        nextPageState: ApiHelper.State.READY,
        errMsg: Helper.getErrorMsg(e),
      })
    }
  }

  onLoadMore = () => {
    this.fetchHistory({ append: true })
  }

  buildTableData() {
    const headers = ['Time', 'From', 'To', 'Change']
    const rows = (this.state.items || []).map((item) => {
      const from = item.previousStatus
      const to = item.currentStatus
      return [
        item.createdTs ? Helper.formatTimestamp(item.createdTs) : '—',
        <HealthStatusCell status={from} />,
        <HealthStatusCell status={to} />,
        (from || to) ? `${from || '—'} → ${to || '—'}` : '—',
      ]
    })
    return {
      headers,
      textColumnIndices: [0, 3],
      rows,
    }
  }

  render() {
    const { fetchState, errMsg, items, nextPage, nextPageState } = this.state

    if (fetchState === ApiHelper.State.LOADING) {
      return (
        <div className={css(Styles.centered)}>
          <CircularProgress size={32} className={css(Styles.progress)} />
        </div>
      )
    }

    if (fetchState === ApiHelper.State.ERROR) {
      return (
        <div className={css(Styles.centered)}>
          <p className={css(Styles.errorText)}>{errMsg || 'Failed to load health history'}</p>
        </div>
      )
    }

    if (!items || items.length === 0) {
      return (
        <div className={css(Styles.centered)}>
          <p className={css(Styles.emptyText)}>No health changes recorded yet.</p>
        </div>
      )
    }

    return (
      <div className={css(Styles.content)}>
        <TableCard
          isModalTable={true}
          compactEmpty={true}
          disableTooltip={true}
          item={{ data: this.buildTableData() }}
        />
        {nextPage && (
          <div className={css(Styles.loadMore)}>
            <AppButton
              onClick={this.onLoadMore}
              buttonText='SHOW MORE'
              buttonStyle={Styles.loadMoreButton}
              buttonTextStyle={Styles.loadMoreButtonText}
              inProcess={nextPageState === ApiHelper.State.LOADING}
              disabled={nextPageState === ApiHelper.State.LOADING}
            />
          </div>
        )}
      </div>
    )
  }
}

export class InboxHealthHistoryDialog extends Component {
  render() {
    const { isOpen, onClosed, orgId, inboxId, inboxEmail } = this.props
    return (
      <PageDialog
        isOpen={!!isOpen}
        title='Inbox Health History'
        PageComponent={InboxHealthHistoryBody}
        PageComponentProps={{ orgId, inboxId, inboxEmail }}
        fullWidth={true}
        maxWidth='md'
        fullWidthDialog={false}
        onClosed={onClosed}
      />
    )
  }
}

/** Global host — listens for CRMHelper.openInboxHealthHistory and renders the dialog. */
export class InboxHealthHistoryHost extends Component {
  constructor(props) {
    super(props)
    this.state = {
      isOpen: false,
      orgId: null,
      inboxId: null,
      inboxEmail: '',
    }
    this.onOpenEvent = this.onOpenEvent.bind(this)
  }

  componentDidMount() {
    window.addEventListener(CRMHelper.INBOX_HEALTH_HISTORY_EVENT, this.onOpenEvent)
  }

  componentWillUnmount() {
    window.removeEventListener(CRMHelper.INBOX_HEALTH_HISTORY_EVENT, this.onOpenEvent)
  }

  onOpenEvent(event) {
    const detail = (event && event.detail) || {}
    if (!detail.orgId || !detail.inboxId) return
    this.setState({
      isOpen: true,
      orgId: detail.orgId,
      inboxId: detail.inboxId,
      inboxEmail: detail.email || '',
    })
  }

  onClosed = () => {
    this.setState({
      isOpen: false,
      orgId: null,
      inboxId: null,
      inboxEmail: '',
    })
  }

  render() {
    const { isOpen, orgId, inboxId, inboxEmail } = this.state
    if (!isOpen) return null
    return (
      <InboxHealthHistoryDialog
        isOpen={isOpen}
        orgId={orgId}
        inboxId={inboxId}
        inboxEmail={inboxEmail}
        onClosed={this.onClosed}
      />
    )
  }
}

const Styles = StyleSheet.create({
  subtitle: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
    wordBreak: 'break-all',
  },
  content: {
    width: '100%',
    paddingBottom: 8,
  },
  centered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 160,
    padding: 24,
  },
  progress: {
    color: AppTheme.primaryColor,
  },
  emptyText: {
    fontSize: 14,
    color: CommonTheme.subTextColor,
    margin: 0,
  },
  errorText: {
    fontSize: 14,
    color: '#d32f2f',
    margin: 0,
    textAlign: 'center',
  },
  statusCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  healthDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
    display: 'inline-block',
  },
  loadMore: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 12,
  },
  loadMoreButton: {
    minWidth: 140,
  },
  loadMoreButtonText: {},
})
